<template>
    <el-config-provider :locale="zhCn">
        <div class="app">
            <el-container>
                <el-header>
                    <div class="header-content">
                        <router-link to="/" class="logo">
                            <Icon icon="mdi:home" width="24" height="24" color="#3699e3" />
                        </router-link>
                        <div class="nav-menu">
                            <el-dropdown trigger="hover">
                                <span class="nav-dropdown-link">
                                    {{ currentPageName }}
                                    <el-icon class="el-icon--right">
                                        <arrow-down />
                                    </el-icon>
                                </span>
                                <template #dropdown>
                                    <el-dropdown-menu>
                                        <el-dropdown-item>
                                            <router-link to="/professor/list" class="dropdown-link">
                                                搜导师
                                            </router-link>
                                        </el-dropdown-item>
                                        <el-dropdown-item>
                                            <router-link to="/square" class="dropdown-link">
                                                留言板
                                            </router-link>
                                        </el-dropdown-item>
                                    </el-dropdown-menu>
                                </template>
                            </el-dropdown>
                        </div>
                        <div class="user-menu">
                            <template v-if="isAuthenticated">
                                <router-link to="/profile" class="nav-item user-profile">
                                    <Icon icon="mdi:account-circle" width="24" height="24" color="#3699e3" />
                                    <span class="username">{{ username }}</span>
                                </router-link>
                            </template>
                            <template v-else>
                                <router-link to="/login" class="nav-item login-btn">
                                    <Icon icon="mdi:login" width="20" height="20" color="#3699e3" />
                                    <span>登录</span>
                                </router-link>
                            </template>
                        </div>
                    </div>
                </el-header>
                
                <el-main>
                    <router-view v-if="isInitialized"/>
                    <div v-else class="loading-container">
                        <el-loading :fullscreen="true"/>
                    </div>
                </el-main>
                
                <el-footer>
                    <div class="footer-content">
                        <p>&copy; 2024 学术村 - 打破信息差, 做出更好的选择</p>
                    </div>
                </el-footer>
            </el-container>
        </div>
    </el-config-provider>
</template>

<script>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { Icon } from '@iconify/vue';
import { ArrowDown } from '@element-plus/icons-vue';
import { useRoute } from 'vue-router';
import zhCn from 'element-plus/dist/locale/zh-cn.mjs';
import { useHead } from '@vueuse/head'

export default {
    name: 'App',
    components: {
        Icon,
        ArrowDown
    },
    setup() {
        const store = useStore();
        const route = useRoute();
        const isInitialized = ref(false);
        
        useHead({
            title: '学术村 - 导师评价网',
            meta: [
                { name: 'description', content: '学术村是一个专注于导师评价、导师推荐、研究生导师选择的学术社区。提供真实可靠的导师评价信息，帮助学生做出更好的选择。' },
                { name: 'keywords', content: '导师评价,研究生导师,导师推荐,导师评分,研究生申请,导师信息,学术评价,研究生导师选择,PhD申请,硕士申请,研究方向,学术氛围,科研能力,学术成果' },
                { property: 'og:title', content: '学术村 - 导师评价与选择平台' },
                { property: 'og:description', content: '提供真实可靠的导师评价信息，帮助学生做出更好的选择。' },
                { property: 'og:type', content: 'website' },
                { name: 'robots', content: 'index, follow' },
                { name: 'author', content: '学术村' },
                { name: 'viewport', content: 'width=device-width, initial-scale=1' },
            ]
        })
        
        const currentPageName = computed(() => {
            switch (route.path) {
                case '/professor/list':
                    return '搜导师';
                case '/square':
                    return '留言板';
                default:
                    return '学术村';
            }
        });
        
        onMounted(async () => {
            if (store.state.token) {
                try {
                    await store.dispatch('fetchCurrentUser');
                } catch (error) {
                    // Token 无效，清除登录状态
                    store.dispatch('logout');
                }
            }
            isInitialized.value = true;
        });
        
        return {
            zhCn,
            isAuthenticated: computed(() => store.getters.isAuthenticated),
            username: computed(() => store.state.user?.username),
            isInitialized,
            currentPageName
        };
    }
};
</script>

<style>
html, body {
    margin: 0;
    padding: 0;
    height: 100%;
    background-color: #f5f7fa;
}

.app {
    min-height: 100vh;
}

.el-container {
    min-height: 100vh;
}

.el-header {
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    position: fixed;
    width: 100%;
    z-index: 100;
    padding: 0;
}

.header-content {
    max-width: 1200px;
    margin: 0 auto;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
}

.logo {
    font-size: 20px;
    font-weight: bold;
    color: #3699e3;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 8px;
}

.logo:hover {
    opacity: 0.9;
}

.nav-menu {
    display: flex;
    gap: 20px;
}

.nav-item {
    color: #3699e3;
    text-decoration: none;
    font-size: 16px;
}

.nav-item:hover {
    color: #3699e3;
}

.user-menu {
    display: flex;
    gap: 20px;
    align-items: center;
}

.user-profile {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 5px 10px;
    border-radius: 4px;
    transition: background-color 0.3s;
}

.user-profile:hover {
    background-color: #f5f7fa;
}

.username {
    font-size: 14px;
    color: #3699e3;
}

.login-btn {
    display: flex;
    align-items: center;
    gap: 4px;
}

.el-main {
    padding-top: 80px;
    min-height: calc(100vh - 60px - 60px);
}

.el-footer {
    background-color: #fff;
    padding: 20px 0;
}

.footer-content {
    max-width: 1200px;
    margin: 0 auto;
    text-align: center;
    color: #909399;
}

.loading-container {
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
}

.nav-dropdown-link {
    color: #3699e3;
    cursor: pointer;
    display: flex;
    align-items: center;
    font-size: 16px;
}

.nav-dropdown-link:hover {
    color: #3699e3;
}

.dropdown-link {
    text-decoration: none;
    color: inherit;
    display: block;
    width: 100%;
}

.el-dropdown-menu__item:hover .dropdown-link {
    color: #3699e3;
}
</style>
